// ROI / terugverdientijd calculator.
//
// Faithful rebuild of the legacy Shuttle tool (tuinaannemers + woningbouw).
// Config comes from window.roiConfig (CMS: roiCategorieen entries). The visitor
// edits the headline inputs + the uren-cells the legacy tool exposed; the
// totaalstation side is derived from the kantelpunt (robot vs. manueel).
//
// Verified against the live tool's tuinaannemers defaults:
//   Conv 32u, TS 5u -> jaarwinst € 24.300 -> 1 jaar en 4 maanden.
//
// Formula:
//   urenBespaard = Σ(conv: uren × pers) − Σ(ts: uren × pers)
//   jaarwinst    = urenBespaard × projecten × uurloon  (+ projecten × landmeterKost als landmeter)
//   kost         = aankoopprijs / afschrijving
//   netto        = jaarwinst − kost
//   terugverdientijd = aankoopprijs / netto → jaren + maanden

const eur = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' });
// Plain 2-decimal amount (no € sign) for the hidden fields — the notification
// templates add the "€ " prefix themselves, so this avoids a double euro.
const amount = new Intl.NumberFormat('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const eur0 = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const num = new Intl.NumberFormat('nl-BE');

// Config numbers use a dot as decimal separator (JSON), never thousands.
const cfgNum = (v) => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

// User input: legacy convention — dots are thousands, comma is decimal.
const userNum = (v) => {
  const n = parseFloat(String(v ?? '').replace(/\s/g, '').replace(/\./g, '').replace(/,/g, '.'));
  return Number.isFinite(n) ? n : 0;
};

function init(root) {
  const config = window.roiConfig;
  if (!config || !Object.keys(config).length) {
    console.warn('[roi-calc] no roiConfig');
    return;
  }

  // All UI strings come from window.roiCopy (rendered via |t('site') in the
  // template, so editable in Translations Admin + per-site translated). NL
  // fallbacks keep things working if the copy block is ever missing.
  const T = window.roiCopy || {};
  const COPY = {
    conventioneel: T.conventioneel || 'Conventioneel',
    robot: T.robot || 'Robot totaalstation',
    manueel: T.manueel || 'Manueel totaalstation',
    personen: T.personen || 'Aantal personen',
    werkstap: T.werkstap || 'Werkstap',
    uren: T.uren || 'Uren',
    totaal: T.totaal || 'Totaal',
    ja: T.ja || 'Ja',
    nee: T.nee || 'Nee',
    jaar: T.jaar || 'jaar',
    jaren: T.jaren || 'jaar',
    maand: T.maand || 'maand',
    maanden: T.maanden || 'maanden',
    en: T.en || 'en',
    direct: T.direct || 'Direct terugverdiend',
    nooit: T.nooit || 'Niet terugverdiend',
    bewerkbaar: T.bewerkbaar || 'aanpasbaar',
    // Admin-email summary labels
    sSector: T.sSector || 'Sector',
    sPrijs: T.sPrijs || 'Aankoopprijs',
    sAfschr: T.sAfschr || 'Afschrijving',
    sProjecten: T.sProjecten || 'Projecten/jaar',
    sLandmeter: T.sLandmeter || 'Landmeter',
    sModus: T.sModus || 'Modus',
    sBespaard: T.sBespaard || 'Bespaarde uren/project',
    sJaarwinst: T.sJaarwinst || 'Jaarwinst',
    sKost: T.sKost || 'Jaarlijkse kost',
    sNetto: T.sNetto || 'Nettowinst',
    sTerug: T.sTerug || 'Terugverdientijd',
  };

  const tabs = [...root.querySelectorAll('.roi-tab')];
  const inputs = {
    aankoopprijs: root.querySelector('[data-input="aankoopprijs"]'),
    afschrijving: root.querySelector('[data-input="afschrijving"]'),
    projecten: root.querySelector('[data-input="projecten"]'),
  };
  const toggleBtns = [...root.querySelectorAll('[data-landmeter]')];
  const tablesEl = root.querySelector('.roi-tables');
  const steps = [...root.querySelectorAll('.roi-step')];
  const results = {
    totconv: root.querySelector('[data-result="totconv"]'),
    totts: root.querySelector('[data-result="totts"]'),
    jaarwinst: root.querySelector('[data-result="jaarwinst"]'),
    kost: root.querySelector('[data-result="kost"]'),
    netto: root.querySelector('[data-result="netto"]'),
    terugverdientijd: root.querySelector('[data-result="terugverdientijd"]'),
  };
  const warningEl = root.querySelector('[data-warning]');
  const form = root.querySelector('form.fui-form');

  // Per-sector working state.
  const state = {};
  Object.values(config).forEach((c) => {
    state[c.slug] = {
      aankoopprijs: cfgNum(c.defaults.aankoopprijs),
      afschrijving: cfgNum(c.defaults.afschrijving),
      projecten: cfgNum(c.defaults.projecten),
      landmeter: false,
      overrides: {}, // rowIndex -> conv-uren
    };
  });

  let active = tabs[0]?.dataset.sector || Object.keys(config)[0];

  // ---------- calculation ----------
  function compute(slug) {
    const c = config[slug];
    const s = state[slug];
    const mode = s.aankoopprijs > cfgNum(c.kantelpunt) ? 'robot' : 'manueel';

    let t1 = 0;
    let t2 = 0;
    const rows = c.taken.map((r, i) => {
      const convUren = i in s.overrides ? s.overrides[i] : cfgNum(r.convUren);
      const convPers = cfgNum(r.convPersonen);
      const tsPers = mode === 'robot' ? cfgNum(r.tsPersonenRobot) : cfgNum(r.tsPersonenManueel);

      let tsUren;
      if (r.tsUrenModus === 'afgeleid') {
        const deler = mode === 'robot' ? cfgNum(r.tsUrenRobot) : cfgNum(r.tsUrenManueel);
        const raw = deler ? convUren / deler : 0;
        tsUren = r.tsAfronding === 'ceil' ? Math.ceil(raw) : Math.round(raw);
      } else {
        tsUren = mode === 'robot' ? cfgNum(r.tsUrenRobot) : cfgNum(r.tsUrenManueel);
      }

      t1 += convUren * convPers;
      t2 += tsUren * tsPers;
      return { taak: r.taak, uitleg: r.uitleg, convUren, convPers, tsUren, tsPers, bewerkbaar: !!r.convBewerkbaar };
    });

    const urenBespaard = t1 - t2;
    const uurloon = cfgNum(c.uurloon);
    let jaarwinst = urenBespaard * s.projecten * uurloon;
    if (s.landmeter) jaarwinst += s.projecten * cfgNum(c.landmeterKost);
    const kost = s.afschrijving ? s.aankoopprijs / s.afschrijving : 0;
    const netto = jaarwinst - kost;
    const factor = netto > 0 ? s.aankoopprijs / netto : Infinity;

    return { c, s, mode, rows, t1, t2, urenBespaard, jaarwinst, kost, netto, factor };
  }

  function paybackText(factor) {
    if (!Number.isFinite(factor)) return COPY.nooit;
    if (factor <= 0) return COPY.direct;
    let jaren = Math.floor(factor);
    let maanden = Math.round((factor - jaren) * 12);
    if (maanden === 12) { jaren += 1; maanden = 0; }
    const parts = [];
    if (jaren > 0) parts.push(`${jaren} ${jaren === 1 ? COPY.jaar : COPY.jaren}`);
    if (maanden > 0) parts.push(`${maanden} ${maanden === 1 ? COPY.maand : COPY.maanden}`);
    if (!parts.length) return COPY.direct;
    return parts.join(` ${COPY.en} `);
  }

  // ---------- rendering ----------
  // One unified "Inschatting gebruiksduur" table, matching the legacy layout:
  // Werkstap | Conventioneel | Aantal personen ¦ Totaalstation | Aantal personen.
  function renderTables(data) {
    const modeLabel = data.mode === 'robot' ? COPY.robot : COPY.manueel;

    const body = data.rows.map((r, i) => {
      const convCell = r.bewerkbaar
        ? `<label class="roi-cell-edit"><input type="text" inputmode="numeric" class="roi-cell-input" data-row="${i}" value="${num.format(r.convUren)}" aria-label="${escapeAttr(r.taak)} — ${COPY.uren} (${COPY.bewerkbaar})"></label>`
        : `<span>${num.format(r.convUren)}</span>`;
      return `<tr>
        <td class="roi-c-taak">${taakLabel(r)}</td>
        <td class="roi-c-num">${convCell}</td>
        <td class="roi-c-num">${num.format(r.convPers)}</td>
        <td class="roi-c-num roi-c-div">${num.format(r.tsUren)}</td>
        <td class="roi-c-num">${num.format(r.tsPers)}</td>
      </tr>`;
    }).join('');

    tablesEl.innerHTML = `
      <div class="roi-table">
        <table class="roi-uren">
          <thead><tr>
            <th>${COPY.werkstap}</th>
            <th>${COPY.conventioneel}</th>
            <th>${COPY.personen}</th>
            <th class="roi-c-div">${modeLabel}</th>
            <th>${COPY.personen}</th>
          </tr></thead>
          <tbody>${body}</tbody>
          <tfoot><tr>
            <td>${COPY.totaal}</td>
            <td class="roi-c-num" data-total="conv">${num.format(data.t1)} u</td>
            <td></td>
            <td class="roi-c-num roi-c-div" data-total="ts">${num.format(data.t2)} u</td>
            <td></td>
          </tr></tfoot>
        </table>
      </div>`;

    // Wire the editable conv-uren cells.
    tablesEl.querySelectorAll('.roi-cell-input').forEach((el) => {
      el.addEventListener('input', () => {
        state[active].overrides[el.dataset.row] = userNum(el.value);
        recalc({ keepTables: true });
      });
      el.addEventListener('blur', () => { recalc(); });
    });
  }

  function renderResult(data) {
    if (results.totconv) results.totconv.textContent = `${num.format(data.t1)} u`;
    if (results.totts) results.totts.textContent = `${num.format(data.t2)} u`;
    if (results.jaarwinst) results.jaarwinst.textContent = eur.format(data.jaarwinst);
    if (results.kost) results.kost.textContent = eur.format(data.kost);
    if (results.netto) results.netto.textContent = eur.format(data.netto);
    if (results.terugverdientijd) results.terugverdientijd.textContent = paybackText(data.factor);
    if (warningEl) warningEl.hidden = data.netto > 0;
  }

  // keepTables avoids re-rendering the whole table (which would blow away the
  // input the user is typing in) — we only patch the totals + result there.
  function recalc(opts = {}) {
    const data = compute(active);
    if (opts.keepTables) {
      const cT = tablesEl.querySelector('[data-total="conv"]');
      const tT = tablesEl.querySelector('[data-total="ts"]');
      if (cT) cT.textContent = `${num.format(data.t1)} u`;
      if (tT) tT.textContent = `${num.format(data.t2)} u`;
      // TS uren cells (4th column) depend on conv edits (derived rows) — patch them.
      tablesEl.querySelectorAll('.roi-uren tbody tr').forEach((tr, i) => {
        const cell = tr.children[3];
        if (cell && data.rows[i]) cell.textContent = num.format(data.rows[i].tsUren);
      });
    } else {
      renderTables(data);
    }
    renderResult(data);
    fillForm(data);
    return data;
  }

  // ---------- Formie hidden fields ----------
  function setField(handle, value) {
    if (!form) return;
    const el = form.querySelector(`[name*="[${handle}]"]`);
    if (el) el.value = value;
  }

  function fillForm(data) {
    if (!form) return;
    setField('sector', active);
    setField('sectorLabel', data.c.label);
    setField('sectorNl', (window.roiSectorNl && window.roiSectorNl[active]) || data.c.label);
    setField('aankoopprijs', num.format(data.s.aankoopprijs));
    setField('afschrijving', num.format(data.s.afschrijving));
    setField('projectenPerJaar', num.format(data.s.projecten));
    setField('landmeter', data.s.landmeter ? COPY.ja : COPY.nee);
    setField('jaarwinst', amount.format(data.jaarwinst));
    setField('jaarlijkseKost', amount.format(data.kost));
    setField('nettowinst', amount.format(data.netto));
    setField('terugverdientijd', paybackText(data.factor));
    setField('samenvatting', buildSummary(data));
    setField('detailJson', buildDetailJson(data));
  }

  // Structured detail for a readable table in the notification emails.
  function buildDetailJson(data) {
    const modeLabel = data.mode === 'robot' ? COPY.robot : COPY.manueel;
    return JSON.stringify({
      mode: modeLabel,
      rows: data.rows.map((r) => ({
        taak: r.taak,
        uitleg: r.uitleg || '',
        cu: num.format(r.convUren),
        cp: num.format(r.convPers),
        tu: num.format(r.tsUren),
        tp: num.format(r.tsPers),
      })),
      t1: num.format(data.t1),
      t2: num.format(data.t2),
      bespaard: num.format(data.urenBespaard),
    });
  }

  function buildSummary(data) {
    const modeLabel = data.mode === 'robot' ? COPY.robot : COPY.manueel;
    const lines = [];
    lines.push(`${COPY.sSector}: ${data.c.label}`);
    lines.push(`${COPY.sPrijs}: ${eur0.format(data.s.aankoopprijs)} | ${COPY.sAfschr}: ${num.format(data.s.afschrijving)} ${COPY.jaren} | ${COPY.sProjecten}: ${num.format(data.s.projecten)} | ${COPY.sLandmeter}: ${data.s.landmeter ? COPY.ja : COPY.nee}`);
    lines.push(`${COPY.sModus}: ${modeLabel}`);
    lines.push('');
    lines.push(`${COPY.conventioneel} (${COPY.uren.toLowerCase()} × ${COPY.personen.toLowerCase()}):`);
    data.rows.forEach((r) => lines.push(`- ${r.taak}: ${num.format(r.convUren)} × ${num.format(r.convPers)}`));
    lines.push(`${COPY.totaal} ${COPY.conventioneel.toLowerCase()}: ${num.format(data.t1)} u`);
    lines.push('');
    lines.push(`${modeLabel}:`);
    data.rows.forEach((r) => lines.push(`- ${r.taak}: ${num.format(r.tsUren)} × ${num.format(r.tsPers)}`));
    lines.push(`${COPY.totaal} ${modeLabel.toLowerCase()}: ${num.format(data.t2)} u`);
    lines.push('');
    lines.push(`${COPY.sBespaard}: ${num.format(data.urenBespaard)}`);
    lines.push(`${COPY.sJaarwinst}: ${eur.format(data.jaarwinst)}`);
    lines.push(`${COPY.sKost}: ${eur.format(data.kost)}`);
    lines.push(`${COPY.sNetto}: ${eur.format(data.netto)}`);
    lines.push(`${COPY.sTerug}: ${paybackText(data.factor)}`);
    return lines.join('\n');
  }

  // ---------- inputs / state sync ----------
  function syncInputsFromState() {
    const s = state[active];
    if (inputs.aankoopprijs) inputs.aankoopprijs.value = num.format(s.aankoopprijs);
    if (inputs.afschrijving) inputs.afschrijving.value = num.format(s.afschrijving);
    if (inputs.projecten) inputs.projecten.value = num.format(s.projecten);
    toggleBtns.forEach((b) => {
      const on = String(s.landmeter ? 1 : 0) === b.dataset.landmeter;
      b.classList.toggle('is-active', on);
    });
  }

  inputs.aankoopprijs?.addEventListener('input', () => { state[active].aankoopprijs = userNum(inputs.aankoopprijs.value); recalc(); });
  inputs.afschrijving?.addEventListener('input', () => { state[active].afschrijving = userNum(inputs.afschrijving.value); recalc(); });
  inputs.projecten?.addEventListener('input', () => { state[active].projecten = userNum(inputs.projecten.value); recalc(); });

  toggleBtns.forEach((b) => {
    b.addEventListener('click', () => {
      state[active].landmeter = b.dataset.landmeter === '1';
      toggleBtns.forEach((x) => x.classList.toggle('is-active', x === b));
      recalc();
    });
  });

  // ---------- tabs ----------
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      if (tab.dataset.sector === active) return;
      active = tab.dataset.sector;
      // Reset this sector to a clean slate on (re)entry — matches the legacy
      // per-category pages, which each opened with their own defaults.
      state[active] = {
        aankoopprijs: cfgNum(config[active].defaults.aankoopprijs),
        afschrijving: cfgNum(config[active].defaults.afschrijving),
        projecten: cfgNum(config[active].defaults.projecten),
        landmeter: false,
        overrides: {},
      };
      tabs.forEach((t) => {
        const on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      syncInputsFromState();
      recalc();
      gotoStep(1);
    });
  });

  // ---------- steps ----------
  function gotoStep(n) {
    steps.forEach((st) => { st.hidden = Number(st.dataset.step) !== n; });
    const target = steps.find((st) => Number(st.dataset.step) === n);
    if (target) {
      const heading = target.querySelector('.roi-step-title');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  root.querySelectorAll('.roi-next, .roi-prev').forEach((btn) => {
    btn.addEventListener('click', () => {
      const to = Number(btn.dataset.goto);
      if (to === 3) recalc(); // make sure hidden fields are fresh before the form
      gotoStep(to);
    });
  });

  // Refresh hidden fields right before submit, in case autofill changed inputs.
  form?.addEventListener('submit', () => { recalc(); }, true);

  // ---------- boot ----------
  syncInputsFromState();
  recalc();
}

// Task label + optional ⓘ info popover (only where the CMS row has `uitleg`),
// mirroring the legacy tool's per-row work descriptions on the conventioneel side.
// Split "Voorstudie (meeturen in offertefase)" into a bold title + italic
// parenthetical, as the legacy tool shows it.
function splitLabel(label) {
  const s = String(label || '');
  const i = s.indexOf('(');
  if (i > 0) return { title: s.slice(0, i).trim(), sub: s.slice(i).trim() };
  return { title: s.trim(), sub: '' };
}

function taakLabel(r) {
  const { title, sub } = splitLabel(r.taak);
  let btn = '';
  if (r.uitleg) {
    const icon = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M12 11v4.2"/><circle cx="12" cy="8" r="0.7" fill="currentColor" stroke="none"/></svg>';
    btn = `<button type="button" class="roi-info" aria-label="Meer info: ${escapeAttr(title)}">${icon}<span class="roi-info-pop" role="tooltip">${escapeHtml(r.uitleg)}</span></button>`;
  }
  const subLine = sub ? `<span class="roi-c-taak-sub">${escapeHtml(sub)}</span>` : '';
  const main = `<span class="roi-c-taak-main"><span class="roi-c-taak-title">${escapeHtml(title)}</span>${subLine}</span>`;
  return `<span class="roi-c-taak-inner">${main}${btn}</span>`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
}
function escapeAttr(s) {
  return escapeHtml(s);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.roi-calc').forEach(init);
});
